import { epicRequest, reducersMerge, reduxFetchCreator, serverResponse } from 'app'
import { FAVORITES_CATEGORIES } from 'users'

import type { ServerResponseSchema } from 'app'
import type { FavoriteSchema } from 'users'

export interface FetchAction {
  categoria: FAVORITES_CATEGORIES
}

type State = {
  loaded: boolean
  loading: boolean
} & Record<FAVORITES_CATEGORIES, ServerResponseSchema<FavoriteSchema[]>>

const getInitialState = () =>
  Object.values(FAVORITES_CATEGORIES).reduce(
    (obj, key) => ({
      ...obj,
      [key]: serverResponse<FavoriteSchema[]>({
        data: [],
      }),
    }),
    {
      loaded: false,
      loading: false,
    } as State,
  )

const unsetLoading = (state: ReturnType<typeof getInitialState>) => {
  Object.values(FAVORITES_CATEGORIES).forEach((key) => {
    state[key as FAVORITES_CATEGORIES].loading = false
  })
}

const favorites = reduxFetchCreator<
  'usersFavoritesFetch',
  FetchAction,
  ReturnType<typeof getInitialState>,
  ServerResponseSchema<FavoriteSchema[]>
>('usersFavoritesFetch', getInitialState(), {
  cancel: unsetLoading,
  error: (state, { payload }) => {
    if (payload.originalAction) {
      const category = payload.originalAction.payload.categoria
      return {
        ...state,
        [category]: {
          ...state[category],
          ...payload,
          loaded: true,
          loading: false,
        },
      }
    }
    return state
  },
  fulfilled: (state, { payload }) => {
    if (payload.originalAction) {
      const category = payload.originalAction.payload.categoria

      return {
        ...state,
        [category]: {
          ...state[category],
          ...payload,
          loaded: true,
          loading: false,
        },
      }
    }
    return state
  },
  start: (state, { payload }) => ({
    ...state,
    [payload.categoria]: {
      ...state[payload.categoria],
      loaded: false,
      loading: true,
    },
  }),
})

const categoriesRequested = new Set<FAVORITES_CATEGORIES>()

const usersFavoritesFetchEpic = epicRequest({
  actionType: favorites.actions.usersFavoritesFetch,
  errorAction: favorites.actions.usersFavoritesFetchError,
  filter: ({ payload }) => {
    if (categoriesRequested.has(payload.categoria)) {
      return false
    }

    categoriesRequested.add(payload.categoria)
    return true
  },
  fulfilledAction: favorites.actions.usersFavoritesFetchFulfilled,
  onComplete: ({ payload }) => {
    if (payload.originalAction?.payload.categoria) {
      categoriesRequested.delete(payload.originalAction.payload.categoria)
    }
  },
  overlap: true,
  request: ({ payload }) => `general/favorito/?categoria=${encodeURIComponent(payload.categoria)}`,
  untilTypes: favorites.actions.usersFavoritesFetchCancel,
})

export interface FetchAddAction {
  key: string
  categoria: FAVORITES_CATEGORIES
  detalle: string
}

const favoriteAdd = reduxFetchCreator<
  'usersFavoritesAdd',
  FetchAddAction,
  ReturnType<typeof getInitialState>,
  ServerResponseSchema<FavoriteSchema>
>('usersFavoritesAdd', getInitialState(), {
  cancel: (state, { payload }) => {
    const category = state[payload.originalAction?.payload.categoria as FAVORITES_CATEGORIES]
    category.loading = false
    category.data = category.data.filter(({ key }) => key !== payload.originalAction?.payload.key)
  },
  error: (state, { payload }) => {
    const category = state[payload.originalAction?.payload.categoria as FAVORITES_CATEGORIES]
    category.loading = false
    category.data = category.data.filter(({ key }) => key !== payload.originalAction?.payload.key)
  },
  fulfilled: (state, { payload }) => {
    const category = state[payload.data.categoria as FAVORITES_CATEGORIES]
    category.loading = false
    category.data = category.data.filter(({ key }) => key !== payload.data.key)
    category.data.push(payload.data)
  },
  start: (state, { payload }) => {
    const category = state[payload.categoria]
    category.loading = true
    category.data = category.data.filter(({ key }) => key !== payload.key)
    category.data.push({
      ...payload,
      id: payload.key,
      idEmpresa: null,
      idUsuario: '',
      usos: 0,
    })
  },
})

const usersFavoritesAddEpic = epicRequest({
  actionType: favoriteAdd.actions.usersFavoritesAdd,
  errorAction: favoriteAdd.actions.usersFavoritesAddError,
  fulfilledAction: favoriteAdd.actions.usersFavoritesAddFulfilled,
  request: ({ payload }) => ({
    body: payload,
    method: 'post',
    url: 'general/favorito',
  }),
  untilTypes: favoriteAdd.actions.usersFavoritesAddCancel,
})

const favoriteUsed = reduxFetchCreator<
  'usersFavoritesUsed',
  {
    id: string
  },
  ReturnType<typeof getInitialState>,
  ServerResponseSchema<FavoriteSchema>
>('usersFavoritesUsed', getInitialState())

const usersFavoritesUsedEpic = epicRequest({
  actionType: favoriteUsed.actions.usersFavoritesUsed,
  errorAction: favoriteUsed.actions.usersFavoritesUsedError,
  fulfilledAction: favoriteUsed.actions.usersFavoritesUsedFulfilled,
  overlap: true,
  request: ({ payload }) => ({
    method: 'put',
    url: `general/favorito/${payload.id}`,
  }),
  untilTypes: favoriteUsed.actions.usersFavoritesUsedCancel,
})

const favoriteDelete = reduxFetchCreator<
  'usersFavoritesDelete',
  {
    id: string
  },
  ReturnType<typeof getInitialState>,
  ServerResponseSchema<FavoriteSchema>
>('usersFavoritesDelete', getInitialState(), {
  cancel: unsetLoading,
  error: unsetLoading,
  fulfilled: unsetLoading,
  start: (state, { payload }) => {
    Object.values(FAVORITES_CATEGORIES).forEach((category) => {
      const index = state[category].data.findIndex(({ id }) => id === payload.id)
      if (index !== -1) {
        state[category].data.splice(index, 1)

        state[category].loading = true
      }
    })
  },
})

const usersFavoritesDeleteEpic = epicRequest({
  actionType: favoriteDelete.actions.usersFavoritesDelete,
  errorAction: favoriteDelete.actions.usersFavoritesDeleteError,
  fulfilledAction: favoriteDelete.actions.usersFavoritesDeleteFulfilled,
  overlap: true,
  request: ({ payload }) => ({
    method: 'delete',
    url: `general/favorito/${payload.id}`,
  }),
  untilTypes: favoriteDelete.actions.usersFavoritesDeleteCancel,
})

export const epics = [usersFavoritesAddEpic, usersFavoritesDeleteEpic, usersFavoritesFetchEpic, usersFavoritesUsedEpic]

export const actions = {
  ...favoriteAdd.actions,
  ...favoriteDelete.actions,
  ...favorites.actions,
  ...favoriteUsed.actions,
}

export const reducer = reducersMerge(
  favoriteAdd.reducer,
  favoriteDelete.reducer,
  favorites.reducer,
  favoriteUsed.reducer,
)
